/**
 * Mailfehler, wie der Betreiber sie lesen soll — statt wie der Mailprovider sie schreibt.
 *
 * ── Warum es diese Datei gibt ────────────────────────────────────────────────────────────────
 * Scheitert der Versand, reicht die Oberflaeche bisher den rohen Fehlertext des Providers durch
 * (`{sendMailError ?? "unbekannter Fehler"}`). Bei den meisten Fehlern ist das richtig: sie sind
 * fluechtig (Zeitueberschreitung, 5xx beim Provider), der Betreiber kann nichts daraus lernen,
 * ausser dass ein zweiter Versuch sinnvoll ist — und genau den bietet der Dialog an.
 *
 * EIN Fehler faellt aus diesem Muster heraus, und zwar der, der das Tagesgeschaeft blockiert:
 * `12206: email_sending_error_email_recipient_suppressed`. Cloudflare fuehrt eine
 * Unterdrueckungsliste (Suppression List); landet eine Adresse dort — nach hartem Bounce oder
 * Beschwerde —, wird JEDE weitere Mail an sie abgewiesen, sofort und dauerhaft. Ein erneuter
 * Versuch aendert daran nichts. Er kann es gar nicht: die Ablehnung passiert, bevor irgendetwas
 * zugestellt wird. Der „E-Mail erneut senden"-Knopf ist hier also kein Angebot, sondern eine
 * Sackgasse — der Betreiber drueckt ihn, weil es das Einzige ist, was dasteht, und bekommt
 * denselben unverstaendlichen String zurueck.
 *
 * ── Was hier NICHT passiert ──────────────────────────────────────────────────────────────────
 * Es wird nicht versucht, jeden Providerfehler zu uebersetzen. Eine Uebersetzungstabelle fuer
 * fremde Fehlertexte veraltet still: der Provider aendert den Wortlaut, die Tabelle trifft nicht
 * mehr, und der Betreiber sieht ploetzlich „unbekannter Fehler" statt der Rohmeldung, die ihm
 * wenigstens beim Nachfragen geholfen haette. Erkannt wird deshalb nur der eine Fall, bei dem die
 * Konsequenz eine ANDERE ist als „nochmal versuchen". Alles uebrige geht unveraendert durch.
 *
 * ── Warum zwei Merkmale statt eines ──────────────────────────────────────────────────────────
 * Der Fehler erreicht die Oberflaeche je nach Weg unterschiedlich verpackt: mal mit Code
 * (`12206`), mal nur mit dem sprechenden Teil (`email_sending_error_email_recipient_suppressed`),
 * mal in einen HTTP-Fehlertext eingebettet. Geprueft wird deshalb auf BEIDES, unabhaengig
 * voneinander und ohne Ruecksicht auf Gross-/Kleinschreibung. Lieber einmal zu oft erkannt als
 * den Betreiber in die Endlosschleife zurueckgeschickt.
 */

/**
 * Die Zahlencodes, die Cloudflare fuer eine unterdrueckte Empfaengeradresse liefert.
 *
 * Es ist NICHT nur einer: gemeldet wurde `12206`, im Produktionsfall vom 24.08.2026 kam
 * `10205` — beide mit demselben sprechenden Teil `recipient_suppressed`. Welche Codes der
 * Dienst sonst noch vergibt, wissen wir nicht; deshalb ist die Liste nur die zweite Reihe.
 * Die erste ist `SUPPRESSION_MUSTER` unten, das den Wortlaut prueft und damit auch einen
 * dritten, noch unbekannten Code faengt. Ein neuer Code gehoert hier trotzdem hinein: er ist
 * die eindeutigere Angabe, und wenn Cloudflare den Wortlaut einmal umformuliert, traegt sie.
 */
const CLOUDFLARE_SUPPRESSION_CODES = ["12206", "10205"] as const;

/**
 * Der sprechende Teil derselben Meldung. Der volle Wortlaut ist
 * `email_sending_error_email_recipient_suppressed`; geprueft wird auf das Kernwort, damit auch
 * gekuerzte oder umformulierte Varianten (`recipient_suppressed`, `recipient suppressed`,
 * `Recipient Suppressed`) greifen. Bewusst nicht auf blosses `suppress`: ein Fehlertext, der das
 * Wort in anderem Zusammenhang fuehrt, soll nicht faelschlich den Wiederholen-Knopf verlieren.
 */
const SUPPRESSION_MUSTER = /suppressed|recipient[_\s-]*suppress/i;

/**
 * Ist dieser Mailfehler die dauerhafte Ablehnung einer Empfaengeradresse?
 *
 * `true` heisst: ein erneuter Versand an dieselbe Adresse ist zwecklos, die Adresse selbst muss
 * geaendert werden. `false` heisst ausdruecklich NICHT „alles in Ordnung", sondern nur „kein
 * bekannter Dauerfehler" — der normale Fehlerweg mit Wiederholung bleibt zustaendig.
 */
export function istEmpfaengerDauerhaftBlockiert(fehler: string | null | undefined): boolean {
  if (!fehler) return false;
  return CLOUDFLARE_SUPPRESSION_CODES.some((code) => fehler.includes(code)) || SUPPRESSION_MUSTER.test(fehler);
}

/**
 * Der anzuzeigende Text zu einem Mailfehler.
 *
 * Antoine, 26.08.2026: seit dem Resend-Fallback (integrations.ts#getOfferMailer) steckt in einem
 * abgelehnten Versand potenziell mehr als der reine Cloudflare-Fehler (z. B. „Cloudflare: ...;
 * Resend-Fallback: ..." bei einem Doppelfehlschlag) — die vorherige Ersetzung durch einen
 * freundlichen Pauschalsatz haette genau diese neue, wichtige Information verschluckt. Der
 * Betreiber sieht deshalb IMMER den exakten Fehlertext; `istEmpfaengerDauerhaftBlockiert` bleibt
 * unveraendert fuer die Frage, ob "erneut senden" ueberhaupt sinnvoll ist (Button/Hinweistext).
 */
export function mailfehlerText(fehler: string | null | undefined): string {
  return fehler ?? "unbekannter Fehler";
}
